import { useCallback, useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { type AreaTableView, type AreaView } from '../../../../services/areas/areas.service';
import { clamp } from '../../core/pos-shell-page.helpers';
import { type AlignmentGapHints, type AlignmentGuides, type CanvasInteraction, type ResizeHandle, type TableFormState, type TableLayoutState } from '../../core/pos-shell-page.types';

const ALIGN_GUIDE_THRESHOLD = 6;

type RectLike = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type AxisMatch = {
  distance: number;
  target: number;
  candidate: number;
};

const toRect = (layout: TableLayoutState): RectLike => ({
  left: layout.positionX,
  right: layout.positionX + layout.width,
  top: layout.positionY,
  bottom: layout.positionY + layout.height,
});

const getAxisAnchors = (start: number, end: number): number[] => [start, (start + end) / 2, end];

const nearestTargetWithinThreshold = (value: number, targets: number[], threshold: number): number | null => {
  let best: { distance: number; target: number } | null = null;
  for (const target of targets) {
    const distance = Math.abs(value - target);
    if (distance <= threshold && (!best || distance < best.distance)) {
      best = { distance, target };
    }
  }
  return best?.target ?? null;
};

const hasVerticalOverlap = (a: RectLike, b: RectLike): boolean => a.top < b.bottom && a.bottom > b.top;
const hasHorizontalOverlap = (a: RectLike, b: RectLike): boolean => a.left < b.right && a.right > b.left;

const edgeGap = (firstStart: number, firstEnd: number, secondStart: number, secondEnd: number): number => {
  if (firstEnd <= secondStart) {
    return secondStart - firstEnd;
  }
  if (secondEnd <= firstStart) {
    return firstStart - secondEnd;
  }
  return 0;
};

const nearestEdgeGap = (
  area: AreaView | undefined,
  movingTableId: number,
  next: TableLayoutState,
): AlignmentGapHints => {
  if (!area) {
    return { horizontal: null, vertical: null };
  }

  const moving = toRect(next);
  let bestHorizontal: number | null = null;
  let bestVertical: number | null = null;

  for (const table of Array.isArray(area.tables) ? area.tables : []) {
    if (table.isActive === false || table.id === movingTableId) {
      continue;
    }
    const other: RectLike = {
      left: table.positionX,
      right: table.positionX + table.width,
      top: table.positionY,
      bottom: table.positionY + table.height,
    };

    if (hasVerticalOverlap(moving, other)) {
      const gap = edgeGap(moving.left, moving.right, other.left, other.right);
      bestHorizontal = bestHorizontal === null ? gap : Math.min(bestHorizontal, gap);
    }

    if (hasHorizontalOverlap(moving, other)) {
      const gap = edgeGap(moving.top, moving.bottom, other.top, other.bottom);
      bestVertical = bestVertical === null ? gap : Math.min(bestVertical, gap);
    }
  }

  return {
    horizontal: bestHorizontal,
    vertical: bestVertical,
  };
};

const resolveAlignmentGuides = (
  area: AreaView | undefined,
  movingTableId: number,
  next: TableLayoutState,
): {
  guides: AlignmentGuides;
  verticalMatch: AxisMatch | null;
  horizontalMatch: AxisMatch | null;
} => {
  if (!area) {
    return {
      guides: { vertical: null, horizontal: null },
      verticalMatch: null,
      horizontalMatch: null,
    };
  }

  const movingRect = toRect(next);
  const movingX = getAxisAnchors(movingRect.left, movingRect.right);
  const movingY = getAxisAnchors(movingRect.top, movingRect.bottom);
  let bestVertical: AxisMatch | null = null;
  let bestHorizontal: AxisMatch | null = null;

  for (const table of Array.isArray(area.tables) ? area.tables : []) {
    if (table.isActive === false || table.id === movingTableId) {
      continue;
    }
    const otherRect = toRect({
      number: table.number,
      shape: table.shape,
      positionX: table.positionX,
      positionY: table.positionY,
      width: table.width,
      height: table.height,
    });
    const otherX = getAxisAnchors(otherRect.left, otherRect.right);
    const otherY = getAxisAnchors(otherRect.top, otherRect.bottom);

    for (const candidate of movingX) {
      for (const target of otherX) {
        const distance = Math.abs(candidate - target);
        if (distance <= ALIGN_GUIDE_THRESHOLD && (!bestVertical || distance < bestVertical.distance)) {
          bestVertical = { distance, target, candidate };
        }
      }
    }

    for (const candidate of movingY) {
      for (const target of otherY) {
        const distance = Math.abs(candidate - target);
        if (distance <= ALIGN_GUIDE_THRESHOLD && (!bestHorizontal || distance < bestHorizontal.distance)) {
          bestHorizontal = { distance, target, candidate };
        }
      }
    }
  }

  return {
    guides: {
      vertical: bestVertical?.target ?? null,
      horizontal: bestHorizontal?.target ?? null,
    },
    verticalMatch: bestVertical,
    horizontalMatch: bestHorizontal,
  };
};

const collectAxisTargets = (
  area: AreaView | undefined,
  movingTableId: number,
): { x: number[]; y: number[] } => {
  if (!area) {
    return { x: [], y: [] };
  }

  const x: number[] = [];
  const y: number[] = [];
  for (const table of Array.isArray(area.tables) ? area.tables : []) {
    if (table.isActive === false || table.id === movingTableId) {
      continue;
    }
    x.push(table.positionX, table.positionX + table.width / 2, table.positionX + table.width);
    y.push(table.positionY, table.positionY + table.height / 2, table.positionY + table.height);
  }
  return { x, y };
};

type UseTableCanvasInteractionParams = {
  areaMode: 'view' | 'edit';
  isManager: boolean;
  selectedTableIds: number[];
  setActiveAreas: Dispatch<SetStateAction<AreaView[]>>;
  setSettingsAreas: Dispatch<SetStateAction<AreaView[]>>;
  settingsAreasRef: MutableRefObject<AreaView[]>;
  setEditingTableId: Dispatch<SetStateAction<number | null>>;
  setTableForm: Dispatch<SetStateAction<TableFormState>>;
  setError: Dispatch<SetStateAction<string | null>>;
  interactionRef: MutableRefObject<CanvasInteraction | null>;
  isPersistingInteractionRef: MutableRefObject<boolean>;
  onAlignmentGuidesChange: Dispatch<SetStateAction<AlignmentGuides>>;
  onAlignmentGapHintsChange: Dispatch<SetStateAction<AlignmentGapHints>>;
  persistTableUpdate: (
    areaId: number,
    tableId: number,
    next: TableLayoutState,
    baseline?: TableLayoutState | null,
  ) => Promise<void>;
};

export function useTableCanvasInteraction({
  areaMode,
  isManager,
  selectedTableIds,
  setActiveAreas,
  setSettingsAreas,
  settingsAreasRef,
  setEditingTableId,
  setTableForm,
  setError,
  interactionRef,
  isPersistingInteractionRef,
  onAlignmentGuidesChange,
  onAlignmentGapHintsChange,
  persistTableUpdate,
}: UseTableCanvasInteractionParams) {
  const applyTableLayoutPreview = useCallback((areaId: number, tableId: number, next: TableLayoutState) => {
    const updater = (areas: AreaView[]) =>
      areas.map((area) => {
        if (area.id !== areaId) {
          return area;
        }

        return {
          ...area,
          tables: (Array.isArray(area.tables) ? area.tables : []).map((table) =>
            table.id === tableId
              ? {
                  ...table,
                  number: next.number,
                  shape: next.shape,
                  positionX: next.positionX,
                  positionY: next.positionY,
                  width: next.width,
                  height: next.height,
                }
              : table,
          ),
        };
      });

    setSettingsAreas((previous) => updater(previous));
    setActiveAreas((previous) => updater(previous));
  }, [setActiveAreas, setSettingsAreas]);

  const applyTableLayoutsPreview = useCallback((
    areaId: number,
    updates: Array<{ tableId: number; next: TableLayoutState }>,
  ) => {
    if (updates.length === 0) {
      return;
    }
    const updatesById = new Map(updates.map((entry) => [entry.tableId, entry.next]));
    const updater = (areas: AreaView[]) =>
      areas.map((area) => {
        if (area.id !== areaId) {
          return area;
        }

        return {
          ...area,
          tables: (Array.isArray(area.tables) ? area.tables : []).map((table) => {
            const next = updatesById.get(table.id);
            if (!next) {
              return table;
            }
            return {
              ...table,
              number: next.number,
              shape: next.shape,
              positionX: next.positionX,
              positionY: next.positionY,
              width: next.width,
              height: next.height,
            };
          }),
        };
      });

    setSettingsAreas((previous) => updater(previous));
    setActiveAreas((previous) => updater(previous));
  }, [setActiveAreas, setSettingsAreas]);

  const beginCanvasInteraction = useCallback((
    event: React.MouseEvent<HTMLElement>,
    areaId: number,
    table: AreaTableView,
    mode: 'move' | 'resize',
    handle?: ResizeHandle,
  ) => {
    if (!isManager || areaMode !== 'edit') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Resolve the canvas element relative to the drag origin. For a resize the
    // event target is a corner handle (child of the table), so the canvas is two
    // levels up; for a move the target is the table itself, so the canvas is its
    // direct parent. Relying on a class name here is fragile because the table
    // class is a hashed CSS-module name, not a stable selector.
    const target = event.currentTarget as HTMLElement;
    const canvas = mode === 'resize' ? target.parentElement?.parentElement : target.parentElement;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const areaTables = settingsAreasRef.current.find((candidate) => candidate.id === areaId)?.tables ?? [];
    const selectedIds = selectedTableIds.includes(table.id)
      ? selectedTableIds
      : [table.id];
    const selectedSet = new Set(selectedIds);
    const groupOrigins = areaTables
      .filter((candidate) => candidate.isActive !== false && selectedSet.has(candidate.id))
      .map((candidate) => ({
        tableId: candidate.id,
        origin: {
          number: candidate.number,
          shape: candidate.shape,
          positionX: candidate.positionX,
          positionY: candidate.positionY,
          width: candidate.width,
          height: candidate.height,
        },
      }));

    interactionRef.current = {
      mode,
      areaId,
      tableId: table.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      canvasWidth: rect.width,
      canvasHeight: rect.height,
      handle,
      origin: {
        number: table.number,
        shape: table.shape,
        positionX: table.positionX,
        positionY: table.positionY,
        width: table.width,
        height: table.height,
      },
      preview: {
        number: table.number,
        shape: table.shape,
        positionX: table.positionX,
        positionY: table.positionY,
        width: table.width,
        height: table.height,
      },
      ...(groupOrigins.length > 1 ? { groupOrigins } : {}),
    };
    setEditingTableId(table.id);
    setTableForm({
      number: table.number,
      shape: table.shape,
    });
    onAlignmentGuidesChange({ vertical: null, horizontal: null });
    onAlignmentGapHintsChange({ horizontal: null, vertical: null });
    setError(null);
  }, [
    areaMode,
    interactionRef,
    isManager,
    selectedTableIds,
    settingsAreasRef,
    onAlignmentGapHintsChange,
    onAlignmentGuidesChange,
    setEditingTableId,
    setError,
    setTableForm,
  ]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const interaction = interactionRef.current;
      if (!interaction || isPersistingInteractionRef.current) {
        return;
      }

      const dx = event.clientX - interaction.startClientX;
      const dy = event.clientY - interaction.startClientY;

      let next: TableLayoutState = {
        ...interaction.origin,
      };

      if (interaction.mode === 'move') {
        const maxX = Math.max(0, interaction.canvasWidth - interaction.origin.width);
        const maxY = Math.max(0, interaction.canvasHeight - interaction.origin.height);
        const rawPositionX = clamp(
          interaction.origin.positionX + dx,
          0,
          maxX,
        );
        const rawPositionY = clamp(
          interaction.origin.positionY + dy,
          0,
          maxY,
        );
        next = {
          ...next,
          positionX: rawPositionX,
          positionY: rawPositionY,
        };
      } else {
        const minSize = 40;
        const right = interaction.origin.positionX + interaction.origin.width;
        const bottom = interaction.origin.positionY + interaction.origin.height;

        if (interaction.handle === 'se') {
          next.width = clamp(
            interaction.origin.width + dx,
            minSize,
            Math.max(minSize, interaction.canvasWidth - interaction.origin.positionX),
          );
          next.height = clamp(
            interaction.origin.height + dy,
            minSize,
            Math.max(minSize, interaction.canvasHeight - interaction.origin.positionY),
          );
        }

        if (interaction.handle === 'ne') {
          next.width = clamp(
            interaction.origin.width + dx,
            minSize,
            Math.max(minSize, interaction.canvasWidth - interaction.origin.positionX),
          );
          next.positionY = clamp(interaction.origin.positionY + dy, 0, bottom - minSize);
          next.height = clamp(bottom - next.positionY, minSize, interaction.canvasHeight);
        }

        if (interaction.handle === 'sw') {
          next.positionX = clamp(interaction.origin.positionX + dx, 0, right - minSize);
          next.width = clamp(right - next.positionX, minSize, interaction.canvasWidth);
          next.height = clamp(
            interaction.origin.height + dy,
            minSize,
            Math.max(minSize, interaction.canvasHeight - interaction.origin.positionY),
          );
        }

        if (interaction.handle === 'nw') {
          next.positionX = clamp(interaction.origin.positionX + dx, 0, right - minSize);
          next.positionY = clamp(interaction.origin.positionY + dy, 0, bottom - minSize);
          next.width = clamp(right - next.positionX, minSize, interaction.canvasWidth);
          next.height = clamp(bottom - next.positionY, minSize, interaction.canvasHeight);
        }

        const areaForResize = settingsAreasRef.current.find((candidate) => candidate.id === interaction.areaId);
        const axisTargets = collectAxisTargets(areaForResize, interaction.tableId);

        if (interaction.handle === 'ne' || interaction.handle === 'se') {
          const snappedRight = nearestTargetWithinThreshold(next.positionX + next.width, axisTargets.x, ALIGN_GUIDE_THRESHOLD);
          if (snappedRight !== null) {
            next.width = clamp(snappedRight - next.positionX, minSize, interaction.canvasWidth - next.positionX);
          }
        }

        if (interaction.handle === 'nw' || interaction.handle === 'sw') {
          const rawLeft = next.positionX;
          const snappedLeft = nearestTargetWithinThreshold(rawLeft, axisTargets.x, ALIGN_GUIDE_THRESHOLD);
          if (snappedLeft !== null) {
            const boundedLeft = clamp(snappedLeft, 0, right - minSize);
            next.positionX = boundedLeft;
            next.width = clamp(right - boundedLeft, minSize, interaction.canvasWidth);
          }
        }

        if (interaction.handle === 'sw' || interaction.handle === 'se') {
          const snappedBottom = nearestTargetWithinThreshold(next.positionY + next.height, axisTargets.y, ALIGN_GUIDE_THRESHOLD);
          if (snappedBottom !== null) {
            next.height = clamp(snappedBottom - next.positionY, minSize, interaction.canvasHeight - next.positionY);
          }
        }

        if (interaction.handle === 'nw' || interaction.handle === 'ne') {
          const rawTop = next.positionY;
          const snappedTop = nearestTargetWithinThreshold(rawTop, axisTargets.y, ALIGN_GUIDE_THRESHOLD);
          if (snappedTop !== null) {
            const boundedTop = clamp(snappedTop, 0, bottom - minSize);
            next.positionY = boundedTop;
            next.height = clamp(bottom - boundedTop, minSize, interaction.canvasHeight);
          }
        }
      }

      const area = settingsAreasRef.current.find((candidate) => candidate.id === interaction.areaId);
      const alignment = resolveAlignmentGuides(area, interaction.tableId, next);
      if (interaction.mode === 'move') {
        if (alignment.verticalMatch) {
          const maxX = Math.max(0, interaction.canvasWidth - next.width);
          next.positionX = clamp(next.positionX + (alignment.verticalMatch.target - alignment.verticalMatch.candidate), 0, maxX);
        }
        if (alignment.horizontalMatch) {
          const maxY = Math.max(0, interaction.canvasHeight - next.height);
          next.positionY = clamp(next.positionY + (alignment.horizontalMatch.target - alignment.horizontalMatch.candidate), 0, maxY);
        }
      }

      let groupPreview: Array<{ tableId: number; next: TableLayoutState }> | null = null;
      if (interaction.groupOrigins && interaction.groupOrigins.length > 1) {
        if (interaction.mode === 'move') {
          const anchorDeltaX = next.positionX - interaction.origin.positionX;
          const anchorDeltaY = next.positionY - interaction.origin.positionY;
          groupPreview = interaction.groupOrigins.map((entry) => {
            const maxX = Math.max(0, interaction.canvasWidth - entry.origin.width);
            const maxY = Math.max(0, interaction.canvasHeight - entry.origin.height);
            return {
              tableId: entry.tableId,
              next: {
                ...entry.origin,
                positionX: clamp(entry.origin.positionX + anchorDeltaX, 0, maxX),
                positionY: clamp(entry.origin.positionY + anchorDeltaY, 0, maxY),
              },
            };
          });
        } else {
          const widthScale = interaction.origin.width > 0 ? next.width / interaction.origin.width : 1;
          const heightScale = interaction.origin.height > 0 ? next.height / interaction.origin.height : 1;
          groupPreview = interaction.groupOrigins.map((entry) => {
            if (entry.tableId === interaction.tableId) {
              return { tableId: entry.tableId, next };
            }
            const maxWidth = Math.max(40, interaction.canvasWidth - entry.origin.positionX);
            const maxHeight = Math.max(40, interaction.canvasHeight - entry.origin.positionY);
            const resizedWidth = clamp(entry.origin.width * widthScale, 40, maxWidth);
            const resizedHeight = clamp(entry.origin.height * heightScale, 40, maxHeight);

            // Preserve layout spacing by scaling table offsets from the resized anchor.
            // This keeps inter-table distances proportional during group resize.
            const rawPositionX = next.positionX + (entry.origin.positionX - interaction.origin.positionX) * widthScale;
            const rawPositionY = next.positionY + (entry.origin.positionY - interaction.origin.positionY) * heightScale;
            const maxX = Math.max(0, interaction.canvasWidth - resizedWidth);
            const maxY = Math.max(0, interaction.canvasHeight - resizedHeight);
            return {
              tableId: entry.tableId,
              next: {
                ...entry.origin,
                positionX: clamp(rawPositionX, 0, maxX),
                positionY: clamp(rawPositionY, 0, maxY),
                width: resizedWidth,
                height: resizedHeight,
              },
            };
          });
        }

        const anchorFromGroup = groupPreview.find((entry) => entry.tableId === interaction.tableId)?.next;
        if (anchorFromGroup) {
          next = anchorFromGroup;
        }
      }
      interaction.preview = next;
      const postSnapAlignment = resolveAlignmentGuides(area, interaction.tableId, next);
      onAlignmentGuidesChange(postSnapAlignment.guides);
      onAlignmentGapHintsChange(nearestEdgeGap(area, interaction.tableId, next));
      if (groupPreview && groupPreview.length > 1) {
        applyTableLayoutsPreview(interaction.areaId, groupPreview);
      } else {
        applyTableLayoutPreview(interaction.areaId, interaction.tableId, next);
      }
    };

    const onMouseUp = async () => {
      const interaction = interactionRef.current;
      if (!interaction || isPersistingInteractionRef.current) {
        return;
      }

      isPersistingInteractionRef.current = true;
      try {
        if (interaction.groupOrigins && interaction.groupOrigins.length > 1) {
          const area = settingsAreasRef.current.find((candidate) => candidate.id === interaction.areaId);
          const currentById = new Map((area?.tables ?? []).map((table) => [table.id, table]));
          for (const entry of interaction.groupOrigins) {
            const current = currentById.get(entry.tableId);
            if (!current) {
              continue;
            }
            await persistTableUpdate(
              interaction.areaId,
              entry.tableId,
              {
                number: current.number,
                shape: current.shape,
                positionX: current.positionX,
                positionY: current.positionY,
                width: current.width,
                height: current.height,
              },
              entry.origin,
            );
          }
        } else {
          await persistTableUpdate(
            interaction.areaId,
            interaction.tableId,
            interaction.preview,
            interaction.origin,
          );
        }
      } catch (interactionError: unknown) {
        if (interactionError instanceof Error) {
          setError(interactionError.message);
        } else {
          setError('Failed saving table layout');
        }
      } finally {
        interactionRef.current = null;
        isPersistingInteractionRef.current = false;
        onAlignmentGuidesChange({ vertical: null, horizontal: null });
        onAlignmentGapHintsChange({ horizontal: null, vertical: null });
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    applyTableLayoutPreview,
    applyTableLayoutsPreview,
    onAlignmentGapHintsChange,
    onAlignmentGuidesChange,
    interactionRef,
    isPersistingInteractionRef,
    persistTableUpdate,
    settingsAreasRef,
    setError,
  ]);

  return {
    beginCanvasInteraction,
  };
}
